import {
  MaterialReactTable,
  MRT_ShowHideColumnsButton as ShowHideColumnsButton,
  MRT_ToggleFiltersButton as ToggleFiltersButton,
  MRT_ToggleFullScreenButton as ToggleFullScreenButton,
  useMaterialReactTable,
} from "material-react-table";

import DatagridTooltipProvider from "../ui/datagridTooltipProvider.js";
import { useSnackbar } from "notistack";
import { useCallback, useEffect, useMemo, useState } from "react";

import { List, Refresh } from "@mui/icons-material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import ClearIcon from "@mui/icons-material/FilterAltOff";
import LaunchIcon from "@mui/icons-material/Launch";
import ClearSortingIcon from "@mui/icons-material/MobiledataOff";
import UndoIcon from "@mui/icons-material/Undo";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  ListItemText,
  MenuItem,
  Tooltip,
  Typography,
} from "@mui/material";
import { alpha, keyframes, useTheme } from "@mui/material/styles";

import {
  EMBED_BOTS,
  LOADER_TEMPLATE,
  buildBotConfig,
  embedLiveUrl,
} from "../../utils/embedScripts";
import {
  publishScripts,
  restoreBackup,
  verifyLiveScripts,
} from "../../utils/embedPublisher";
import {
  mrtGetActionColumnStyling,
  mrtGetActionIconSx,
  mrtGetBodyRowSx,
  mrtGetBottomToolbarSx,
  mrtGetContainerSx,
  mrtGetPaperSx,
  mrtGetTableWrapperSx,
  mrtGetTopToolbarFlexSx,
} from "../../utils/helpers";
import OmniResponsiveButton from "../ui/omniResponsiveButton";
import Widget from "../ui/widget";
import WidgetSurveyContent from "../ui/widgetSurveyContent";
import WidgetTitle from "../ui/widgetTitle";

const STATUS_LABELS = {
  checking: "Checking…",
  "up-to-date": "Up To Date",
  "needs-update": "Needs Update",
  missing: "Not Published",
  error: "Error",
};

const LAST_BACKUP_KEY = "omniEmbedLastBackup";

const spin = keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
`;

const readLastBackup = () => {
  try {
    const raw = window.localStorage.getItem(LAST_BACKUP_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (error) {
    return null;
  }
};

const persistLastBackup = value => {
  try {
    if (value) {
      window.localStorage.setItem(LAST_BACKUP_KEY, JSON.stringify(value));
    } else {
      window.localStorage.removeItem(LAST_BACKUP_KEY);
    }
  } catch (error) {
    return;
  }
};

const InjectionScripts = props => {
  const { widgetSize = "large", minHeight = "333px" } = props;

  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();

  const [verifyResults, setVerifyResults] = useState({});
  const [isVerifying, setIsVerifying] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);
  const [isUndoing, setIsUndoing] = useState(false);

  const [confirmBots, setConfirmBots] = useState(null);
  const [lastBackup, setLastBackup] = useState(readLastBackup);
  const [columnOrder, setColumnOrder] = useState([
    "mrt-row-select",
    "mrt-row-actions",
    "displayName",
    "liveVersion",
    "status",
  ]);

  const runVerification = useCallback(async () => {
    setIsVerifying(true);
    const checking = {};
    EMBED_BOTS.forEach(bot => {
      checking[bot.key] = { key: bot.key, status: "checking" };
    });
    setVerifyResults(checking);

    try {
      const results = await verifyLiveScripts(EMBED_BOTS, (key, result) => {
        setVerifyResults(previous => ({ ...previous, [key]: result }));
      });
      setVerifyResults(results);
    } finally {
      setIsVerifying(false);
    }
  }, []);

  useEffect(() => {
    runVerification();
  }, [runVerification]);

  const statusSx = useCallback(
    status => {
      switch (status) {
        case "up-to-date":
          return {
            backgroundColor: theme.palette.success.main,
            color: theme.palette.success.contrastText,
          };
        case "needs-update":
          return {
            backgroundColor: theme.palette.warning.main,
            color: theme.palette.warning.contrastText,
          };
        case "error":
          return {
            backgroundColor: theme.palette.error.main,
            color: theme.palette.error.contrastText,
          };
        case "missing":
          return {
            backgroundColor: theme.palette.info.main,
            color: theme.palette.info.contrastText,
          };
        default:
          return {
            backgroundColor: theme.palette.action.disabledBackground,
            color: theme.palette.text.secondary,
          };
      }
    },
    [theme],
  );

  const rows = useMemo(
    () =>
      EMBED_BOTS.map(bot => {
        const result = verifyResults[bot.key] || { status: "checking" };
        return {
          key: bot.key,
          bot,
          displayName: bot.displayName,
          expectedVersion: result.expectedVersion || bot.botpressVersion || "v3.6",
          liveVersion: result.liveVersion || null,
          status: result.status || "checking",
          liveUrl: embedLiveUrl(bot),
        };
      }),
    [verifyResults],
  );

  const outOfDateCount = rows.filter(
    row => row.status === "needs-update" || row.status === "missing",
  ).length;

  const handleUndo = useCallback(async () => {
    if (!lastBackup) {
      return;
    }
    setIsUndoing(true);
    try {
      const bots = EMBED_BOTS.filter(bot => lastBackup.keys.includes(bot.key));
      await restoreBackup(lastBackup.backupFolder, bots);
      enqueueSnackbar("Reverted to the previous version.", { variant: "success" });
      setLastBackup(null);
      persistLastBackup(null);
      runVerification();
    } catch (error) {
      enqueueSnackbar(`Undo failed: ${error.message}`, { variant: "error" });
    } finally {
      setIsUndoing(false);
    }
  }, [lastBackup, enqueueSnackbar, runVerification]);

  const handlePublish = useCallback(
    async bots => {
      setIsPublishing(true);
      try {
        const outcome = await publishScripts(bots);
        setConfirmBots(null);
        const count = `${bots.length} script${bots.length === 1 ? "" : "s"}`;
        if (outcome.backupFolder) {
          const record = {
            backupFolder: outcome.backupFolder,
            count,
            keys: bots.map(bot => bot.key),
          };
          setLastBackup(record);
          persistLastBackup(record);
          enqueueSnackbar(
            `Published ${count} — previous version${bots.length === 1 ? "" : "s"} backed up to ${outcome.backupFolder}. Use Undo to revert.`,
            { variant: "success", autoHideDuration: 12000 },
          );
        } else {
          enqueueSnackbar(
            `Published ${count}, but the server did not confirm a backup. Verify a backup exists before relying on undo.`,
            { variant: "warning", autoHideDuration: 15000 },
          );
        }
        runVerification();
      } catch (error) {
        enqueueSnackbar(`Update failed: ${error.message}`, { variant: "error" });
      } finally {
        setIsPublishing(false);
      }
    },
    [enqueueSnackbar, runVerification],
  );

  const columns = useMemo(() => {
    const cellBorder = `1px solid ${alpha(theme.palette.commonground.contrastText, 0.5)}`;
    return [
      {
        accessorKey: "displayName",
        header: "Client Bot",
        size: 240,
        grow: true,
        enableColumnFilter: true,
        filterVariant: "text",
        muiTableBodyCellProps: {
          sx: { verticalAlign: "middle", paddingLeft: theme.spacing(2), borderLeft: cellBorder },
        },
        Cell: ({ row }) => (
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
              {row.original.displayName}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {row.original.key}.js
            </Typography>
          </Box>
        ),
      },
      {
        accessorKey: "liveVersion",
        header: "Version",
        size: 140,
        grow: true,
        enableColumnFilter: true,
        filterVariant: "text",
        muiTableHeadCellProps: { align: "center" },
        muiTableBodyCellProps: {
          align: "center",
          sx: { verticalAlign: "middle", borderLeft: cellBorder },
        },
        Cell: ({ row }) => {
          const { liveVersion, expectedVersion, status } = row.original;
          return (
            <Box sx={{ textAlign: "center" }}>
              <Typography variant="body2">{liveVersion || "—"}</Typography>
              {status === "needs-update" && (
                <Typography variant="caption" color="warning.main">
                  target {expectedVersion}
                </Typography>
              )}
            </Box>
          );
        },
      },
      {
        accessorKey: "status",
        header: "Status",
        size: 170,
        grow: true,
        enableColumnFilter: true,
        filterFn: "equals",
        filterVariant: "select",
        filterSelectOptions: [
          { label: "Up To Date", value: "up-to-date" },
          { label: "Needs Update", value: "needs-update" },
          { label: "Not Published", value: "missing" },
          { label: "Error", value: "error" },
        ],
        muiTableHeadCellProps: { align: "center" },
        muiTableBodyCellProps: {
          align: "center",
          sx: { verticalAlign: "middle", borderLeft: cellBorder },
        },
        Cell: ({ cell }) => {
          const status = cell.getValue();
          return (
            <Box
              component="span"
              sx={{
                ...statusSx(status),
                borderRadius: "0.25rem",
                display: "inline-block",
                minWidth: "120px",
                textAlign: "center",
                padding: theme.spacing(0.75),
              }}
            >
              <Typography variant="button" sx={{ fontWeight: "bold", fontSize: "0.72rem" }}>
                {STATUS_LABELS[status] || status}
              </Typography>
            </Box>
          );
        },
      },
    ];
  }, [statusSx, theme]);

  const table = useMaterialReactTable({
    columns,
    data: rows,
    enableColumnActions: false,
    enableColumnFilters: true,
    enableColumnFilterModes: false,
    enableColumnOrdering: false,
    enableColumnPinning: false,
    enableDensityToggle: false,
    enableFacetedValues: true,
    enableFullScreenToggle: true,
    enableGlobalFilter: false,
    enableGrouping: false,
    enableHiding: true,
    enablePagination: true,
    enableRowActions: true,
    enableRowSelection: true,
    enableSorting: true,
    enableStickyHeader: true,
    enableTopToolbar: true,
    enableBottomToolbar: true,
    paginationDisplayMode: "pages",
    enableFirstPageButton: false,
    enableLastPageButton: false,
    layoutMode: "grid",
    getRowId: row => row.key,
    initialState: {
      density: "compact",
      pagination: { pageSize: 20, pageIndex: 0 },
      columnOrder: [
        "mrt-row-select",
        "mrt-row-actions",
        "displayName",
        "liveVersion",
        "status",
      ],
    },
    state: {
      isLoading: isVerifying && Object.keys(verifyResults).length === 0,
      columnOrder,
    },
    onColumnOrderChange: setColumnOrder,
    defaultColumn: { minSize: 20, maxSize: 9001, size: 50, grow: false },
    muiPaginationProps: {
      color: "primary",
      shape: "rounded",
      showRowsPerPage: true,
      variant: "outlined",
    },
    muiTopToolbarProps: {
      sx: {
        ...mrtGetTopToolbarFlexSx(),
        backgroundColor: theme.palette.commonground.main,
        color: theme.palette.secondary.main,
        "& .MuiIconButton-root": {
          marginLeft: theme.spacing(1),
          backgroundColor: theme.palette.commonground.main,
          color: theme.palette.secondary.main,
          border: `2px solid ${theme.palette.secondary.main}`,
          "&:hover": {
            color: theme.palette.secondary.main,
            backgroundColor: theme.palette.secondary.contrastText,
            border: `2px solid ${theme.palette.secondary.main}`,
          },
          "&.Mui-disabled": {
            border: `2px solid ${theme.palette.action.disabledBackground}`,
          },
        },
        "& .MuiIconButton-root-active": {
          marginLeft: theme.spacing(1),
          color: theme.palette.secondary.contrastText,
          backgroundColor: theme.palette.secondary.main,
          border: `2px solid ${theme.palette.secondary.main}`,
          "&:hover": {
            color: theme.palette.secondary.main,
            backgroundColor: theme.palette.secondary.contrastText,
            border: `2px solid ${theme.palette.secondary.main}`,
          },
        },
      },
    },
    muiBottomToolbarProps: { sx: mrtGetBottomToolbarSx(theme) },
    muiTablePaperProps: { sx: mrtGetPaperSx() },
    muiTableContainerProps: { sx: mrtGetContainerSx(theme, false) },
    muiTableProps: {
      sx: {
        backgroundColor: theme.palette.commonground.main,
        color: theme.palette.commonground.main,
      },
    },
    muiTableBodyRowProps: {
      sx: mrtGetBodyRowSx(),
    },
    muiTableHeadCellProps: {
      sx: {
        backgroundColor: alpha(theme.palette.commonground.contrastText, 0.12),
        color: theme.palette.commonground.contrastText,
        justifyContent: "start",
      },
    },
    displayColumnDefOptions: {
      "mrt-row-actions": {
        ...mrtGetActionColumnStyling(),
        muiTableBodyCellProps: {
          align: "center",
          sx: {
            paddingRight: theme.spacing(2),
            borderLeft: `1px solid ${alpha(theme.palette.commonground.contrastText, 0.5)}`,
          },
        },
      },
      "mrt-row-select": {
        size: 50,
        grow: false,
        muiTableHeadCellProps: { align: "center" },
        muiTableBodyCellProps: {
          align: "center",
          sx: { color: theme.palette.secondary.main },
        },
      },
    },
    icons: {
      MoreHorizIcon: () => <List color="secondary" sx={mrtGetActionIconSx(theme)} />,
    },
    renderRowActionMenuItems: ({ row, closeMenu }) => [
      <MenuItem
        key="publish"
        disabled={isPublishing || isVerifying}
        onClick={() => {
          setConfirmBots([row.original.bot]);
          closeMenu();
        }}
      >
        <CloudUploadIcon fontSize="large" color="secondary" sx={{ marginRight: theme.spacing(1) }} />
        <ListItemText disableTypography={true}>
          <Typography variant="h5" color="textSecondary">
            Update this file
          </Typography>
        </ListItemText>
      </MenuItem>,
      <MenuItem
        key="open"
        onClick={() => {
          window.open(row.original.liveUrl, "_blank");
          closeMenu();
        }}
      >
        <LaunchIcon fontSize="large" color="secondary" sx={{ marginRight: theme.spacing(1) }} />
        <ListItemText disableTypography={true}>
          <Typography variant="h5" color="textSecondary">
            Open live file
          </Typography>
        </ListItemText>
      </MenuItem>,
    ],
    renderToolbarInternalActions: ({ table: mrtTable }) => {
      const tableState = mrtTable.getState();
      return (
        <Box>
          {tableState.sorting.length > 0 && (
            <Tooltip title="Clear All Sorting" arrow>
              <IconButton
                className="MuiIconButton-root-active"
                onClick={() => mrtTable.resetSorting()}
              >
                <ClearSortingIcon />
              </IconButton>
            </Tooltip>
          )}
          {tableState.columnFilters.length > 0 && (
            <Tooltip title="Clear All Filters" arrow>
              <IconButton
                className="MuiIconButton-root-active"
                onClick={() => mrtTable.resetColumnFilters()}
              >
                <ClearIcon />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip
            title={
              lastBackup
                ? `Restore ${lastBackup.count} from ${lastBackup.backupFolder}`
                : "No update to undo yet"
            }
            arrow
          >
            <span>
              <IconButton
                disabled={!lastBackup || isUndoing || isPublishing || isVerifying}
                onClick={handleUndo}
              >
                <UndoIcon />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Re-verify against live" arrow>
            <span>
              <IconButton onClick={runVerification} disabled={isVerifying || isPublishing}>
                <Refresh
                  sx={{ animation: isVerifying ? `${spin} 0.8s linear infinite` : "none" }}
                />
              </IconButton>
            </span>
          </Tooltip>
          <ToggleFiltersButton table={mrtTable} />
          <ShowHideColumnsButton table={mrtTable} />
          <ToggleFullScreenButton table={mrtTable} />
        </Box>
      );
    },
    renderTopToolbarCustomActions: ({ table: mrtTable }) => {
      const selected = mrtTable.getSelectedRowModel().rows;
      return (
        <Box sx={{ display: "flex", gap: "16px", paddingTop: "4px", flexWrap: "wrap" }}>
          <OmniResponsiveButton
            icon={<CloudUploadIcon />}
            tooltipTitle="Regenerate & publish the selected files"
            buttonText="Update Selected"
            disabled={selected.length < 1 || isPublishing || isVerifying}
            onClick={() => setConfirmBots(selected.map(row => row.original.bot))}
          />
          <OmniResponsiveButton
            icon={<CloudUploadIcon />}
            tooltipTitle="Regenerate & publish every file"
            buttonText="Update All"
            disabled={isPublishing || isVerifying}
            onClick={() => setConfirmBots(EMBED_BOTS)}
          />
        </Box>
      );
    },
  });

  const confirmCount = confirmBots ? confirmBots.length : 0;
  const isSingle = confirmCount === 1;
  const confirmTitle = isSingle
    ? `Update “${confirmBots[0].displayName}”`
    : `Update ${confirmCount} injection script${confirmCount === 1 ? "" : "s"}`;

  return (
    <Widget widgetSize={widgetSize} minHeight={minHeight}>
      <WidgetTitle
        title="Injection Scripts"
        text={
          isVerifying
            ? "Verifying against live…"
            : `${EMBED_BOTS.length} scripts · ${outOfDateCount} need updating`
        }
        loading={isVerifying && Object.keys(verifyResults).length === 0}
      />

      <WidgetSurveyContent>
        <Box sx={mrtGetTableWrapperSx(0, false)}>
          <DatagridTooltipProvider>
            <MaterialReactTable table={table} />
          </DatagridTooltipProvider>
        </Box>
      </WidgetSurveyContent>

      <Dialog
        open={confirmBots != null}
        onClose={() => !isPublishing && setConfirmBots(null)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>{confirmTitle}</DialogTitle>
        <DialogContent dividers>
          <DialogContentText component="div" sx={{ mb: 2 }}>
            This regenerates {isSingle ? "this file" : "every selected file"} from the hardcoded
            template below and overwrites {isSingle ? "it" : "them"} in S3, then invalidates
            CloudFront. The current live file{isSingle ? "" : "s"} will be copied to a timestamped{" "}
            <code>backups/</code> folder first, and this update can be undone.
          </DialogContentText>

          <Alert severity="warning" sx={{ mb: 2 }}>
            A bad file is a client-facing point of failure. Review the template before confirming.
          </Alert>

          <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
            Files to update ({confirmCount})
          </Typography>
          <Box
            sx={{
              maxHeight: 120,
              overflowY: "auto",
              mb: 2,
              p: 1,
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: 1,
            }}
          >
            {(confirmBots || []).map(bot => (
              <Typography key={bot.key} variant="body2">
                {bot.key}.js — {bot.displayName} — {buildBotConfig(bot).botpressVersion}
              </Typography>
            ))}
          </Box>

          <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
            Config template
          </Typography>
          <Box
            component="pre"
            sx={{
              m: 0,
              p: 1.5,
              maxHeight: 260,
              overflow: "auto",
              backgroundColor: theme.palette.grey[900],
              color: theme.palette.grey[100],
              borderRadius: 1,
              fontSize: "0.72rem",
              lineHeight: 1.4,
              whiteSpace: "pre",
            }}
          >
            {LOADER_TEMPLATE}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmBots(null)} disabled={isPublishing}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="secondary"
            disabled={isPublishing}
            onClick={() => handlePublish(confirmBots)}
          >
            {isPublishing ? "Publishing…" : isSingle ? "Update file" : "Update files"}
          </Button>
        </DialogActions>
      </Dialog>
    </Widget>
  );
};

export default InjectionScripts;
